export const currency = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(
    Number(n) || 0
  );

export const todayISO = () => {
  const d = new Date();
  d.setHours(12, 0, 0, 0);
  return d.toISOString().slice(0, 10);
};

/** Current wall-clock time in the user's local timezone as "HH:MM". */
export const nowHHMM = () => {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

/**
 * True when a slot starting at `startTime` on `date` is in the past relative to
 * the user's local clock. Only same-day slots can be expired; every future date
 * is always valid. `now` is injectable so callers that already track a ticking
 * clock stay consistent within a render.
 */
export const isSlotExpired = (date, startTime, now = nowHHMM()) =>
  date === todayISO() && startTime <= now;

export const addDays = (iso, n) => {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

export function formatDate(iso, opts = {}) {
  if (!iso) return '';
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', ...opts });
}

export function formatDayLabel(iso) {
  const today = todayISO();
  if (iso === today) return 'Today';
  if (iso === addDays(today, 1)) return 'Tomorrow';
  if (iso === addDays(today, -1)) return 'Yesterday';
  return formatDate(iso, { weekday: 'short' });
}

/** "14:30" -> "2:30 PM" */
export function formatTime(hhmm) {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const suffix = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${suffix}`;
}

export const timeRange = (a, b) => `${formatTime(a)} – ${formatTime(b)}`;

export function relativeTime(ts) {
  if (!ts) return '';
  const then = new Date(ts.includes('T') ? ts : `${ts.replace(' ', 'T')}Z`);
  const diff = (Date.now() - then.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

/** Drops a leading honorific so "Sr. Lakshmi Pillai" reads as "Lakshmi". */
const withoutHonorific = (name = '') => name.replace(/^(Sr|Sister|Dr|Mr|Mrs|Ms|Prof)\.?\s+/i, '').trim();

export const initials = (name = '') =>
  withoutHonorific(name)
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

export const firstName = (name = '') => withoutHonorific(name).split(/\s+/)[0] || name;

/** Maps a stored avatar key to its public URL, or null when there is none. */
export const avatarUrl = (key) => (key ? `/api/avatars/${key}` : null);

/** URL for an uploaded service-icon image, or null when only an emoji is set. */
export const serviceIconUrl = (key) => (key ? `/api/service-icons/${key}` : null);

export const csvList = (s) =>
  String(s || '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

export const STATUS_LABEL = {
  pending: 'Awaiting confirmation',
  accepted: 'Confirmed',
  declined: 'Declined',
  cancelled: 'Cancelled',
  completed: 'Completed',
};
